import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import http from 'utils/http';
import { createSnack } from 'snackbar/snackbarSlice';
import { formatISO } from 'date-fns';

import { loadingManagementApi } from '../../loadingManagementApi';
import { ANY_OPTION, ANY_VALUE } from '../../utils/const';

const initialState = {
  activeAuthSequencingTab: 'baseProducts',
  authorizationSequencingCards: [],
  authorizationSequencingDetails: {
    command: {},
    dataCompany: '',
    futureSequencedAuthorizationDate: {},
    futureSequencedAuthorizations: [],
    futureUnSequencedAuthorizations: [],
    location: {},
    product: {},
    sequencedAuthorizations: [],
    supplier: {},
    unsequencedAuthorizations: [],
  },
  authorizationSequencingDetailsPending: false,
  authorizationSequencingPending: true,
  editDetailsError: '',
  editDetailsSuccess: false,
  filtersOpen: false,
  selectorsList: [],
  selectorsListPending: true,
};

const reducers = {
  resetAuthSequencing: R.always(initialState),
  setActiveAuthSequencingTab: R.assoc('activeAuthSequencingTab'),
  setAuthorizationSequencingCards: R.assoc('authorizationSequencingCards'),
  setAuthorizationSequencingDetails: R.assoc('authorizationSequencingDetails'),
  setAuthorizationSequencingDetailsPending: R.assoc('authorizationSequencingDetailsPending'),
  setAuthorizationSequencingPending: R.assoc('authorizationSequencingPending'),
  setEditDetailsSuccess: R.assoc('editDetailsSuccess'),
  setFiltersOpen: R.assoc('filtersOpen'),
  setSelectorsList: R.assoc('selectorsList'),
  setSelectorsListPending: R.assoc('selectorsListPending'),
  updateSequence: (val, state) => {
    return R.assocPath(['authorizationSequencingDetails', val.key], val.value, state);
  },
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'authorizationSequencing',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

export const {
  resetAuthSequencing,
  setActiveAuthSequencingTab,
  setAuthorizationSequencingCards,
  setAuthorizationSequencingPending,
  setAuthorizationSequencingDetails,
  setAuthorizationSequencingDetailsPending,
  setEditDetailsSuccess,
  setFiltersOpen,
  setSelectorsList,
  setSelectorsListPending,
  updateSequence,
} = actions;

// Selectors
const mergePath = R.concat(['loadingManagement', 'authorizationSequencing']);
export const getStoreValueByProp = R.o(R.path, mergePath);

export const getSelectorsData = createSelector(
  R.path(['loadingManagement', 'filters', 'searchTerm']),
  getStoreValueByProp(['selectorsList']),
  (searchTerm, dataList) => {
    if (searchTerm) {
      return dataList.filter(({ nameShort }) =>
        nameShort.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return dataList;
  }
);

export const getCurrentSelection = createSelector(
  getStoreValueByProp(['selectorsList']),
  R.path(['loadingManagement', 'filters', 'selectedIndex']),
  (selectorsData, selectedIndex) => selectorsData[selectedIndex] || {}
);

// CardPanel
const getAllCards = getStoreValueByProp(['authorizationSequencingCards']);

export const getActiveAuthSequencingTab = getStoreValueByProp(['activeAuthSequencingTab']);
// getAuthorizationSequencingCards :: string, array -> array
export const getAuthorizationSequencingCards = createSelector(
  getAllCards,
  getActiveAuthSequencingTab,
  (array, tabName) => R.propOr(array, tabName, array)
);

const filterTransformations = {
  loadingAllowed: R.equals('loadingAllowed'),
};
const getFilters = R.compose(
  // transform the loadingManagement filter to match data
  R.evolve(filterTransformations),
  // remove any filters that do not have a value chosen
  R.filter(R.o(R.not, R.equals(ANY_VALUE))),
  // only grab filters applicable to Auth Sequencing
  R.pick(['loadingAllowed', 'nameShort', 'state']),
  R.path(['loadingManagement', 'filters'])
);

// getCardsToDisplay :: object, array -> array
export const getCardsToDisplay = createSelector(
  getFilters,
  getAuthorizationSequencingCards,
  // return cards that match the filters
  // eslint-disable-next-line react-hooks/rules-of-hooks
  R.useWith(R.filter, [R.whereEq])
);

// returns an object with counts for each list tab
// getListTabCounts :: array -> object
export const getListTabCounts = createSelector(getAllCards, cards => {
  // please do not rename these consts
  // this is so they easily map for our
  // ListTabs component
  const Base = cards?.baseProducts?.length;
  const Custom = cards?.customProducts?.length;
  return { Base, Custom };
});

// filter option arrays
export const getProductOrLocationOptions = createSelector(
  getAuthorizationSequencingCards,
  R.compose(
    R.prepend(ANY_OPTION),
    R.map(({ nameShort }) => ({ label: nameShort, value: nameShort })),
    R.uniqBy(R.prop('nameShort'))
  )
);
export const getStateOptions = createSelector(
  getAuthorizationSequencingCards,
  R.compose(
    R.prepend(ANY_OPTION),
    R.map(({ state }) => ({ label: state, value: state })),
    R.sortBy(R.prop('state')),
    R.uniqBy(R.prop('state'))
  )
);

// Actions
let fetchAuthorizationSequencingCardsController;
// isAuthSequencingUpdatesFlagOn is passed from Sidebar so this thunk can handle
// enhanced response shape without calling feature-flag helpers in slice selectors.
export const fetchAuthorizationSequencingCards =
  (queryParams, type, optionalParam, isAuthSequencingUpdatesFlagOn = false) =>
  dispatch => {
    if (fetchAuthorizationSequencingCardsController)
      fetchAuthorizationSequencingCardsController.abort();

    fetchAuthorizationSequencingCardsController = new AbortController();

    const { signal } = fetchAuthorizationSequencingCardsController;

    const paramString = getStringifiedQueryParams(queryParams);
    const URL = `authorization-sequencing/view-${type}-authorization-sequencing?${paramString}`;
    dispatch(setAuthorizationSequencingPending(true));
    dispatch(setAuthorizationSequencingCards([]));
    if (!optionalParam) dispatch(setActiveAuthSequencingTab('baseProducts'));
    http
      .get(URL, { signal })
      .then(({ data }) => {
        if (!data) return data;

        // Feature flag: REACT_APP_LD_SEQUENCING_AUTH_LIST
        if (
          isAuthSequencingUpdatesFlagOn &&
          type === 'location' &&
          Array.isArray(data?.combinedProducts)
        ) {
          dispatch(setActiveAuthSequencingTab('combinedProducts'));
        }

        dispatch(setAuthorizationSequencingCards(data));
        return data;
      })
      .finally(() => dispatch(setAuthorizationSequencingPending(false)));
  };

let fetchUniqueSelectorsController;
export const fetchUniqueSelectors = (queryParams, type) => dispatch => {
  dispatch(setAuthorizationSequencingPending(true));
  dispatch(setSelectorsListPending(true));

  // reset data to avoid fetching cards twice
  dispatch(setSelectorsList([]));

  // reset cards
  dispatch(setAuthorizationSequencingCards([]));

  if (fetchUniqueSelectorsController) fetchUniqueSelectorsController.abort();

  fetchUniqueSelectorsController = new AbortController();

  const { signal } = fetchUniqueSelectorsController;

  const paramString = getStringifiedQueryParams(queryParams);

  const URL = `authorization-sequencing/get-unique-${type}s?${paramString}`;
  // return the promise data so that we can see if
  // data was returned for the sidebar to determine
  // if the cards panel needs to render the no available
  // card or a list of cards
  return http
    .get(URL, { signal })
    .then(({ data }) => {
      data && dispatch(setSelectorsList(data));
      return data;
    })
    .finally(() => dispatch(setSelectorsListPending(false)));
};

export const fetchAuthorizationSequencingDetails = queryParams => dispatch => {
  const paramString = getStringifiedQueryParams(queryParams);
  const URL = `authorization-sequencing/edit?${paramString}`;
  dispatch(setAuthorizationSequencingDetailsPending(true));
  dispatch(setEditDetailsSuccess(false));
  http
    .get(URL)
    .then(({ data }) => {
      dispatch(setAuthorizationSequencingDetails(data));
      return data;
    })
    .finally(() => dispatch(setAuthorizationSequencingDetailsPending(false)));
};

export const updateAuthorizationSequencingDetails = () => (dispatch, getState) => {
  const URL = 'authorization-sequencing/edit';
  const {
    activeAuthSequencingTab,
    authorizationSequencingCards,
    authorizationSequencingDetails: {
      supplier: { dataCompanyId, companyCustomerIdName },
      location: { id: locationId },
      product: { id: productId },
      sequencedAuthorizations,
      futureSequencedAuthorizations,
      futureUnSequencedAuthorizations,
      futureSequencedAuthorizationDate,
    },
  } = getState().loadingManagement.authorizationSequencing;

  const futureAuthorizationsObj = {
    futureAuthorizationSequence: [],
    futureSequenceDate: null,
  };
  const futureAuthorizationSequenceToRemove = R.pluck(
    'productAuthorizationId',
    futureUnSequencedAuthorizations ?? []
  );

  if (futureSequencedAuthorizations.length) {
    futureAuthorizationsObj.futureAuthorizationSequence = R.pluck(
      'productAuthorizationId',
      futureSequencedAuthorizations.filter(
        auth => !futureAuthorizationSequenceToRemove.includes(auth.productAuthorizationId)
      )
    );
    futureAuthorizationsObj.futureSequenceDate = formatISO(futureSequencedAuthorizationDate);
  }

  const command = {
    authorizationSequence: R.pluck('productAuthorizationId', sequencedAuthorizations),
    futureSequence: futureAuthorizationsObj,
    locationId,
    productId,
    supplier: { companyCustomerIdName, dataCompanyId },
  };

  http
    .post(URL, {
      ...command,
      futureSequence: {
        futureAuthorizationSequence: futureAuthorizationSequenceToRemove,
        futureSequenceDate: null,
      },
    })
    .then(removed => {
      if (removed.status !== 200) throw new Error('Unable to save sequence.');
      http
        .post(URL, command)
        .then(({ data }) => {
          dispatch(setAuthorizationSequencingDetails(data));
          dispatch(setEditDetailsSuccess(true));

          // Update the card locally with transformed data
          // The edit endpoint returns inventoryOwner as objects, but cards need strings
          const cardsArr =
            authorizationSequencingCards[activeAuthSequencingTab] ?? authorizationSequencingCards;

          const idx = R.findIndex(R.whereEq({ locationId, productId }))(cardsArr);

          if (idx !== -1) {
            // Transform authorization data to match card format
            // Edit endpoint uses 'active' but cards use 'isActive'
            const transformAuth = auth => ({
              ...auth,
              inventoryOwner:
                typeof auth.inventoryOwner === 'string'
                  ? auth.inventoryOwner
                  : auth.inventoryOwner?.nameShort ||
                    auth.inventoryOwner?.companyCustomerIdName ||
                    '',
              isActive: auth.active,
            });

            const updatedCard = {
              ...cardsArr[idx],
              notSequenced: (data.unsequencedAuthorizations || []).map(transformAuth),
              sequenced: (data.sequencedAuthorizations || []).map(transformAuth),
            };

            const updatedCards = R.update(idx, updatedCard, cardsArr);

            // Handle both data structures: Object with tabs or flat array
            const updatedCardsArr = Array.isArray(authorizationSequencingCards)
              ? updatedCards
              : {
                  ...authorizationSequencingCards,
                  [activeAuthSequencingTab]: updatedCards,
                };

            dispatch(setAuthorizationSequencingCards(updatedCardsArr));
          }

          return data;
        })
        .finally(() => {
          dispatch(setAuthorizationSequencingDetailsPending(false));
          dispatch(
            createSnack({
              message: 'Authorization Sequencing has been updated',
            })
          );
          dispatch(setEditDetailsSuccess(false));
        });
    })
    .catch(() => {
      dispatch(
        createSnack({
          message: 'An error occured updating this sequence',
          variant: 'error',
        })
      );
    });
};

// Dedicated thunk used by the EditFutureSequenceDrawer flow.
// Returns a promise resolving to `true` on success, `false` on failure.
export const updateFutureAuthorizationSequence =
  ({ futureSequencedAuthorizationDate, futureSequencedAuthorizations = [] }) =>
  async (dispatch, getState) => {
    const URL = 'authorization-sequencing/edit';
    const withCurrentTime = date => {
      const selectedDate = new Date(date);
      const now = new Date();

      selectedDate.setHours(
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        now.getMilliseconds()
      );

      return selectedDate;
    };
    const normalizeInventoryOwner = inventoryOwner => {
      if (typeof inventoryOwner === 'string') return inventoryOwner;

      return inventoryOwner?.nameShort || inventoryOwner?.companyCustomerIdName || '';
    };
    const {
      activeAuthSequencingTab,
      authorizationSequencingCards,
      authorizationSequencingDetails: {
        supplier: { dataCompanyId, companyCustomerIdName },
        location: { id: locationId },
        product: { id: productId },
        sequencedAuthorizations,
        unsequencedAuthorizations,
      },
    } = getState().loadingManagement.authorizationSequencing;

    const nextFutureAuthorizationIds = R.pluck(
      'productAuthorizationId',
      futureSequencedAuthorizations
    );

    // Compute which future authorizations were removed by comparing original state to incoming
    const allAuthorizations = [
      ...(sequencedAuthorizations || []),
      ...(unsequencedAuthorizations || []),
    ];
    const originalFutureIds = allAuthorizations
      .filter(a => !!a.futureSequenceNumber)
      .map(a => a.productAuthorizationId);
    const nextFutureIdSet = new Set(nextFutureAuthorizationIds);
    const removedFutureIds = originalFutureIds.filter(id => !nextFutureIdSet.has(id));

    const futureAuthorizationsObj = futureSequencedAuthorizations.length
      ? {
          futureAuthorizationSequence: nextFutureAuthorizationIds,
          futureSequenceDate: formatISO(withCurrentTime(futureSequencedAuthorizationDate)),
        }
      : { futureAuthorizationSequence: [], futureSequenceDate: null };

    const command = {
      authorizationSequence: R.pluck('productAuthorizationId', sequencedAuthorizations),
      futureSequence: futureAuthorizationsObj,
      locationId,
      productId,
      supplier: { companyCustomerIdName, dataCompanyId },
    };

    dispatch(setAuthorizationSequencingDetailsPending(true));

    try {
      // If there are removed future authorizations, first send a removal call
      if (removedFutureIds.length) {
        const removalResponse = await http.post(URL, {
          ...command,
          futureSequence: {
            futureAuthorizationSequence: removedFutureIds,
            futureSequenceDate: null,
          },
        });

        if (removalResponse.status !== 200) {
          throw new Error('Unable to save future sequence.');
        }
      }
      const { data } = await http.post(URL, command);

      dispatch(setAuthorizationSequencingDetails(data));
      dispatch(setEditDetailsSuccess(true));

      const cardsArr =
        authorizationSequencingCards[activeAuthSequencingTab] ?? authorizationSequencingCards;
      const idx = R.findIndex(R.whereEq({ locationId, productId }))(cardsArr);

      if (idx !== -1) {
        const transformAuth = auth => ({
          ...auth,
          inventoryOwner: normalizeInventoryOwner(auth.inventoryOwner),
          isActive: auth.active,
        });

        const updatedCard = {
          ...cardsArr[idx],
          futureSequenceDate: futureAuthorizationsObj.futureSequenceDate,
          notSequenced: (data.unsequencedAuthorizations || []).map(transformAuth),
          sequenced: (data.sequencedAuthorizations || []).map(transformAuth),
        };

        const updatedCards = R.update(idx, updatedCard, cardsArr);
        const updatedCardsArr = Array.isArray(authorizationSequencingCards)
          ? updatedCards
          : {
              ...authorizationSequencingCards,
              [activeAuthSequencingTab]: updatedCards,
            };

        dispatch(setAuthorizationSequencingCards(updatedCardsArr));
      }
      // Invalidate RTK Query cache to trigger a full refetch of the card list
      dispatch(loadingManagementApi.util.invalidateTags(['AuthorizationSequencing']));

      dispatch(
        createSnack({
          message: 'Future Authorization Sequencing has been updated',
          title: 'Action Completed',
        })
      );
      return true;
    } catch {
      dispatch(
        createSnack({
          message: 'An error occured updating this future sequence',
          options: { variant: 'error' },
          title: 'Action Not Completed',
        })
      );

      return false;
    } finally {
      dispatch(setAuthorizationSequencingDetailsPending(false));
      dispatch(setEditDetailsSuccess(false));
    }
  };

export default reducer;
